// Taken from district-bloom version 0.12.1

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  LayoutChangeEvent,
  Modal,
  PanResponder,
  ScrollView,
  StyleProp,
  StyleSheet,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";

import { BMCard, BMPrimitives, useBMTheme } from "@traveloka/district-bloom";

import BMOverlay from "./BMOverlay";

type TrayProps = {
  containerStyle?: StyleProp<ViewStyle>;
  /**
   * Tray content
   */
  children?: React.ReactNode;
  isVisible: boolean;
  onTrayClose?: () => void;
  /**
   * Custom component for tray footer.
   */
  footerComponent?: () => React.ReactNode;
  /**
   * Initial height of the tray when it is open. Value is in pixel.
   * If not specified, it will take default value (35% of screen height)
   * Maximum tray height still applied (85% of the screen height)
   * @default "35% of the screen height"
   */
  initialHeight?: number;
  testID?: string;
  dismissible?: boolean;
  /**
   * Defines whether tray height will hug the content or not.
   * Tray max height would still be 85% of screen height
   * if false, minHeight/initialHeight will be applied
   * @default false
   */
  fitContent?: boolean;
  headerComponent?: () => React.ReactNode;
  showScrollIndicator?: boolean;
};

const BMTray = (props: TrayProps) => {
  const {
    containerStyle,
    children,
    onTrayClose,
    footerComponent,
    initialHeight,
    testID,
    isVisible: visible,
    dismissible = true,
    fitContent = false,
    headerComponent,
    showScrollIndicator = true,
  } = props;
  const { BMComponents } = useBMTheme();
  const [isVisible, setIsVisible] = useState(visible);
  const { height: windowHeight } = useWindowDimensions();
  const cardRadiusStyle = footerComponent
    ? {
        borderTopLeftRadius: BMComponents.BMStyleCardRadiusSpacious,
        borderTopRightRadius: BMComponents.BMStyleCardRadiusSpacious,
      }
    : {
        borderRadius: BMComponents.BMStyleCardRadiusSpacious,
      };
  const maxHeight = useRef<number>(0.85);
  const [minHeight, setMinHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const calculateFooterHeight = (e: LayoutChangeEvent) => {
    const { height } = e.nativeEvent.layout;

    const totalHeight = height ? height : 0; //padding size of spacious card
    setFooterHeight(totalHeight);
  };
  const calculateContentHeight = (e: LayoutChangeEvent) => {
    const { height } = e.nativeEvent.layout;
    const offset = 54; // header extra padding
    setContentHeight(height + offset);
  };
  const handleTrayHeight = () => {
    if (fitContent) {
      setMinHeight(Math.ceil((contentHeight / windowHeight) * 100) / 100);
    } else {
      const min = (windowHeight * 0.35 - footerHeight) / windowHeight;

      if (initialHeight) {
        const initHeight = initialHeight / windowHeight;
        setMinHeight(Math.max(min, initHeight));
      } else {
        setMinHeight(min);
      }
    }

    maxHeight.current = (windowHeight * 0.85 - footerHeight) / windowHeight;
  };

  // handlers
  const handleClose = () => {
    staticDraggedHeightPercentage.current = 0;
    staticHeight.current = 0;

    const { x1, y1, x2, y2 } = BMPrimitives.BMAnimation.transition.easeInOut;
    Animated.parallel([
      Animated.timing(cardOpacityAnimation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
        easing: Easing.bezier(x1, y1, x2, y2),
      }),
      Animated.timing(heightAnimation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
        easing: Easing.bezier(x1, y1, x2, y2),
      }),
    ]).start(() => {
      setIsVisible(false);
      onTrayClose?.();
    });
  };

  const handleOpen = () => {
    const { easeInOut, easeOutBounce } = BMPrimitives.BMAnimation.transition;

    if (minHeight > 0) {
      Animated.parallel([
        Animated.timing(cardOpacityAnimation, {
          toValue: 1,
          duration: 400,
          useNativeDriver: false,
          easing: Easing.bezier(
            easeInOut.x1,
            easeInOut.y1,
            easeInOut.x2,
            easeInOut.y2
          ),
        }),
        Animated.timing(heightAnimation, {
          toValue: Math.min(maxHeight.current, minHeight),
          duration: 400,
          useNativeDriver: false,
          easing: Easing.bezier(
            easeOutBounce.x1,
            easeOutBounce.y1,
            easeOutBounce.x2,
            easeOutBounce.y2
          ),
        }),
      ]).start();
    }
  };

  // animations
  const cardOpacityAnimation = useRef(new Animated.Value(0)).current;
  const heightAnimation = useRef(new Animated.Value(0)).current;
  // This is the static value if modal height and pointer. This is used to calculate the dragged percentage of height based on the pointer dif
  const staticDraggedHeightPercentage = useRef(0);
  const staticHeight = useRef(0);

  const cardOpacityInterpolation = cardOpacityAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });
  const heightInterpolation = heightAnimation.interpolate({
    inputRange: [0, 0.35, 0.85],
    outputRange: [0, windowHeight * 0.35, windowHeight * 0.85],
  });

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => (fitContent ? false : true),
        onMoveShouldSetPanResponder: () => (fitContent ? false : true),
        onPanResponderMove: (_, gestureState) => {
          const val = (gestureState.y0 + gestureState.dy) / windowHeight;

          const { x1, y1, x2, y2 } = BMPrimitives.BMAnimation.transition.linear;

          let draggedHeightPosDif = 0;
          if (staticDraggedHeightPercentage.current === 0) {
            staticDraggedHeightPercentage.current = val;
          } else {
            draggedHeightPosDif = val - staticDraggedHeightPercentage.current;
          }

          if (staticHeight.current === 0) {
            // @ts-ignore
            staticHeight.current = heightAnimation._value;
          }

          const draggedPercentageVal = Math.min(
            maxHeight.current,
            staticHeight.current - draggedHeightPosDif
          );

          Animated.timing(heightAnimation, {
            toValue: draggedPercentageVal,
            duration: 0,
            useNativeDriver: false,
            easing: Easing.bezier(x1, y1, x2, y2),
          }).start();

          if (draggedPercentageVal < minHeight) {
            const opacityPercentage = draggedPercentageVal / minHeight;

            Animated.parallel([
              Animated.timing(cardOpacityAnimation, {
                toValue: opacityPercentage,
                duration: 0,
                useNativeDriver: false,
                easing: Easing.bezier(x1, y1, x2, y2),
              }),
            ]).start();
          }
          return true;
        },
        onPanResponderRelease: (_, gestureState) => {
          const { x1, y1, x2, y2 } =
            BMPrimitives.BMAnimation.transition.easeOutBounce;

          if (staticDraggedHeightPercentage.current !== 0) {
            staticDraggedHeightPercentage.current = 0;
          }

          if (staticHeight.current !== 0) {
            staticHeight.current = 0;
          }

          if (gestureState.dy < 0) {
            Animated.timing(heightAnimation, {
              toValue: maxHeight.current,
              duration: 200,
              useNativeDriver: false,
              easing: Easing.bezier(x1, y1, x2, y2),
            }).start();
          }
          // @ts-ignore
          else if (heightAnimation._value < minHeight) {
            handleClose();
          } else {
            Animated.timing(heightAnimation, {
              toValue: minHeight,
              duration: 200,
              useNativeDriver: false,
              easing: Easing.bezier(x1, y1, x2, y2),
            }).start();
          }
          return true;
        },
      }),
    [minHeight]
  );

  const renderFooter = () => {
    if (!footerComponent) return null;

    return (
      <View onLayout={calculateFooterHeight}>
        <BMCard
          layout="SPACIOUS"
          elevation="FLOAT"
          style={[styles.footerContainer]}
          customStyle={{
            borderRadius: 0,
            borderBottomLeftRadius: BMComponents.BMStyleCardRadiusSpacious,
            borderBottomRightRadius: BMComponents.BMStyleCardRadiusSpacious,
          }}
        >
          {footerComponent()}
        </BMCard>
      </View>
    );
  };

  const renderHeader = () => {
    if (!headerComponent) return null;

    return <View style={styles.header}>{headerComponent()}</View>;
  };

  useEffect(() => {
    if (visible && !isVisible) {
      setIsVisible(true);
      handleOpen();
    } else if (!visible && isVisible) {
      handleClose();
    }
  }, [visible]);

  useEffect(() => {
    if ((footerHeight || !footerComponent) && contentHeight) handleTrayHeight();
  }, [footerHeight, contentHeight]);

  useEffect(() => {
    handleOpen();
  }, [minHeight]);

  return (
    <View>
      <Modal
        visible={isVisible}
        onRequestClose={handleClose}
        animationType={"none"}
        transparent
      >
        <Animated.View
          style={[
            styles.contentContainer,
            containerStyle,
            {
              opacity: cardOpacityInterpolation,
            },
          ]}
        >
          <Animated.View
            style={{
              height: heightInterpolation,
              position: "relative",
            }}
          >
            <BMCard
              layout="SPACIOUS"
              elevation="FLOAT"
              customStyle={[styles.card, cardRadiusStyle]}
            >
              {/* header  */}
              <View
                style={styles.trayHandleArea}
                {...panResponder.panHandlers}
                testID={`${testID}-handle`}
              >
                <View
                  style={[
                    styles.trayHandle,
                    {
                      backgroundColor: BMComponents.BMStyleCardBordercolor,
                    },
                  ]}
                />
              </View>
              {renderHeader()}

              {/* content  */}
              <ScrollView
                showsVerticalScrollIndicator={showScrollIndicator}
                showsHorizontalScrollIndicator={showScrollIndicator}
                scrollEnabled
                style={{ width: "100%" }}
              >
                <View
                  onLayout={calculateContentHeight}
                  style={[styles.content, { width: "100%" }]}
                >
                  {children}
                </View>
              </ScrollView>
            </BMCard>
          </Animated.View>

          {/* sticky footer */}
          {renderFooter()}
        </Animated.View>

        <BMOverlay
          dismissible={dismissible}
          visible={isVisible}
          onDismiss={handleClose}
        />
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    width: "100%",
  },
  contentContainer: {
    zIndex: 997,
    position: "absolute",
    paddingHorizontal: BMPrimitives.BMSize.md,
    width: "100%",
  },
  trayHandle: {
    width: 48,
    height: 4,
    borderRadius: BMPrimitives.BMRadius.rounded,
  },
  trayHandleArea: {
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: 0,
    width: "100%",
    paddingVertical: 16,
  },
  card: {
    borderRadius: 0,
    flex: 1,
    alignItems: "center",
  },
  overlay: {
    flex: 1,
  },
  footerContainer: {
    zIndex: 999,
  },
  content: {
    marginTop: BMPrimitives.BMSize.sm,
    flex: 1,
    width: "100%",
    position: "absolute",
  },
  hidden: {
    //@ts-ignore
    visibility: "hidden",
    opacity: 0,
    zIndex: -10,
    position: "absolute",
    top: 0,
    left: 0,
  },
});

export default BMTray;
